function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { ButtonCancel, ButtonSave } from '@arc/frontend/ui-buttons';
import { PasswordInput } from '@arc/frontend/ui-form';
import { LoadingIndicator } from '@arc/frontend/ui-graphics';
import { Modal } from '@arc/frontend/ui-modal';
import { theme, typography } from '@arc/frontend/ui-theme';
import { createErrorToast, createSuccessToast } from '@arc/frontend/ui-toasts';
import { changePassword, useCheckResetPasswordTokenQuery, useMutationLogin, useRedirectFromWelcomePage } from '@arc/frontend/util-auth';
import { ChangePasswordFormDto } from '@arc/shared/util-auth';
import { RESET_PASSWORD_MODAL_PARAM } from './ResetPassword';
const formSchema = ChangePasswordFormDto;
const styles = {
  inputField: process.env.NODE_ENV === "production" ? {
    name: "akdqam",
    styles: "margin-top:1rem"
  } : {
    name: "p0uj5u-inputField",
    styles: "margin-top:1rem;label:inputField;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  },
  icon: /*#__PURE__*/css({
    color: theme.colors.lightBlue,
    marginLeft: '0.5rem',
    cursor: 'pointer'
  }, process.env.NODE_ENV === "production" ? "" : ";label:icon;"),
  subtitle: /*#__PURE__*/css("margin-bottom:1.25rem;", typography.subTitle, process.env.NODE_ENV === "production" ? "" : ";label:subtitle;")
};
export function WelcomeView() {
  const {
    t
  } = useTranslation(['login', 'user-form']);
  const navigate = useNavigate();
  const {
    mutateAsync: requestLogin
  } = useMutationLogin();
  const formMethods = useForm({
    defaultValues: {
      confirmPassword: '',
      newPassword: ''
    },
    resolver: superstructResolver(formSchema, {
      coerce: true,
      mask: true
    })
  });
  const token = useParams().token;
  const {
    data: checkTokenData,
    isPending: tokenResponsePending
  } = useCheckResetPasswordTokenQuery(token);
  const {
    isValidToken,
    user
  } = checkTokenData ?? {};
  const navigateToLogin = useCallback(() => {
    void navigate({
      pathname: '/login',
      search: `?${RESET_PASSWORD_MODAL_PARAM}=t`
    });
  }, [navigate]);
  const handleInvalidToken = useCallback(() => {
    createErrorToast(t('login:invalid password reset link'), {
      autoClose: false
    });
    navigateToLogin();
  }, [navigateToLogin, t]);
  useEffect(() => {
    if (!tokenResponsePending && !isValidToken) {
      handleInvalidToken();
    }
  }, [handleInvalidToken, tokenResponsePending, isValidToken]);
  const redirectFromWelcomePage = useRedirectFromWelcomePage();
  const handleChangePassword = formMethods.handleSubmit(async data => {
    try {
      const responseData = await changePassword({
        newPassword: data.newPassword,
        token
      });
      const {
        email
      } = responseData;
      await requestLogin({
        email,
        password: data.newPassword
      });
      // This is a hack, but I could not get anything else to work in a
      // reasonable amount of time. -Joshua
      redirectFromWelcomePage();
      toast.dismiss();
      createSuccessToast(t('login:Your password has been successfully changed'), 10);
    } catch {
      handleInvalidToken();
    }
  });
  return !tokenResponsePending && isValidToken ? <Modal show={true} onRequestClose={navigateToLogin} dialogStyleOverrides={{
    width: '340px'
  }}>
			<form onSubmit={handleChangePassword}>
				<Modal.Header>{t('login:Welcome', {
          user
        })}</Modal.Header>
				<Modal.Body>
					<div css={styles.subtitle}>{t('login:Please set your password')}</div>
					<FormProvider {...formMethods}>
						<PasswordInput name="newPassword" label={t('login:New password')} placeholder={t('login:Enter new password')} autoFocus data-testid="new-password" />

						<div css={styles.inputField}>
							<PasswordInput name="confirmPassword" label={t('login:Confirm password')} placeholder={t('login:Confirm password')} data-testid="confirm-password" />
						</div>
					</FormProvider>
				</Modal.Body>
				<Modal.Footer>
					<ButtonCancel onClick={navigateToLogin} />
					<ButtonSave type="submit">{t('login:Set password')}</ButtonSave>
				</Modal.Footer>
			</form>
		</Modal> : <LoadingIndicator />;
}